// Derived match state computed from the sim snapshot + current selection.
// Grouped together because they all re-derive from the same inputs each tick
// and share the "scan scene.children" pattern.

import type { GameSnapshot, SceneNode } from '@aod/protocol';
import type { PlayerHudEntry } from '@aod/ui';
import { useMemo } from 'react';
import { cityOwnerId } from './ownership.js';
import type { Selection } from './testBridge/useTestBridge.js';

interface UseDerivedMatchStateOptions {
  snapshot: GameSnapshot | null;
  selection: Selection;
  playerHudEntries: PlayerHudEntry[];
}

export interface DerivedMatchState {
  selected: SceneNode | null;
  unitNodes: SceneNode[];
  buildings: BuildingCatalogEntry[];
  playerColors: Map<number, string>;
  garrisonCount: number;
}

export interface BuildingCatalogEntry {
  id: string;
  name: string;
  description: string;
  cost: number;
  buildTime: number;
}

export function useDerivedMatchState(opts: UseDerivedMatchStateOptions): DerivedMatchState {
  const { snapshot, selection, playerHudEntries } = opts;

  // Resolve the selected SceneNode against the current snapshot. We do this
  // by id (not by holding a node reference) so it stays in sync as the sim
  // re-emits snapshots each tick.
  const selected = useMemo<SceneNode | null>(() => {
    if (!selection || !snapshot) return null;
    const all = snapshot.scene.children ?? [];
    return all.find((n) => n.id === selection.id) ?? null;
  }, [selection, snapshot]);

  const unitNodes = useMemo<SceneNode[]>(() => {
    return (snapshot?.scene.children ?? []).filter((n) => n.type === 'unit');
  }, [snapshot]);

  const buildings = useMemo<BuildingCatalogEntry[]>(() => {
    const globals = snapshot?.state.globals as { buildings?: BuildingCatalogEntry[] } | undefined;
    return globals?.buildings ?? [];
  }, [snapshot]);

  const playerColors = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of playerHudEntries) {
      m.set(p.playerId, p.color);
    }
    return m;
  }, [playerHudEntries]);

  // Count garrisoned units at the selected city's coord (units with same
  // q/r and same owner).
  const garrisonCount = useMemo(() => {
    if (!selected || !snapshot) return 0;
    const sq = selected.props?.q as number | undefined;
    const sr = selected.props?.r as number | undefined;
    if (sq === undefined || sr === undefined) return 0;
    const ownerId =
      selected.type === 'city'
        ? cityOwnerId(selected.props)
        : (selected.props?.ownerId as number | undefined);
    if (ownerId === undefined) return 0;
    return (
      snapshot.scene.children?.filter(
        (n) =>
          n.type === 'unit' &&
          (n.props?.ownerId as number | undefined) === ownerId &&
          (n.props?.q as number | undefined) === sq &&
          (n.props?.r as number | undefined) === sr,
      ).length ?? 0
    );
  }, [selected, snapshot]);

  return { selected, unitNodes, buildings, playerColors, garrisonCount };
}
